import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SimulatedQr } from "@/components/SimulatedQr";
import {
  adminGetOrder,
  adminOrderAction,
  adminGetShipment,
  adminAddShipmentEvent,
} from "@/lib/orders-admin.functions";
import { deliveryLabel, kr, statusLabel, statusTone, shipmentStatusLabel } from "@/lib/orders";

export const Route = createFileRoute("/_authenticated/admin/ordrar_/$id")({
  component: AdminOrderDetail,
  head: () => ({
    meta: [{ title: "Order – administration" }, { name: "robots", content: "noindex" }],
  }),
});

const ACTIONS: { action: string; label: string }[] = [
  { action: "label", label: "Skapa fraktsedel" },
  { action: "in_transit", label: "Markera på väg" },
  { action: "awaiting_pickup", label: "Markera väntar på hämtning" },
  { action: "delivered", label: "Markera levererad" },
  { action: "release", label: "Betala ut till säljaren" },
  { action: "refund", label: "Återbetala köparen" },
  { action: "dispute", label: "Öppna tvist" },
  { action: "cancel", label: "Avbryt order" },
];

function AdminOrderDetail() {
  const { id } = Route.useParams();
  const fetchOrder = useServerFn(adminGetOrder);
  const act = useServerFn(adminOrderAction);
  const fetchShipment = useServerFn(adminGetShipment);
  const addEvent = useServerFn(adminAddShipmentEvent);
  const qc = useQueryClient();
  const [note, setNote] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-order", id],
    queryFn: () => fetchOrder({ data: { id } }),
  });

  const { data: shipment } = useQuery({
    queryKey: ["admin-shipment", id],
    queryFn: () => fetchShipment({ data: { id } }),
  });

  const stepMut = useMutation({
    mutationFn: (status: string) => addEvent({ data: { id, status: status as any } }),
    onSuccess: () => {
      toast.success("Kollihändelsen är tillagd.");
      qc.invalidateQueries({ queryKey: ["admin-shipment", id] });
      qc.invalidateQueries({ queryKey: ["admin-order", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mut = useMutation({
    mutationFn: (action: string) => act({ data: { id, action: action as any, note: note.trim() } }),
    onSuccess: () => {
      toast.success("Klart.");
      setNote("");
      qc.invalidateQueries({ queryKey: ["admin-order", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading || !data) {
    return (
      <AdminShell title="Order">
        <p className="text-muted-foreground">Laddar…</p>
      </AdminShell>
    );
  }

  const o = data.order as Record<string, any>;

  return (
    <AdminShell title="Order">
      <Link to="/admin/ordrar" className="inline-flex items-center gap-2 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Alla ordrar
      </Link>

      <header className="mt-4 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-bold">Order #{o["order_no"]}</h1>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone(o["status"])}`}>
          {statusLabel(o["status"])}
        </span>
        {o["is_simulated"] && (
          <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-600">
            Övning
          </span>
        )}
      </header>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-border bg-card p-4 text-sm">
          <h2 className="font-heading text-lg font-semibold">Affären</h2>
          <p className="mt-2">
            <Link to="/a/$slug" params={{ slug: o["ad_slug"] }} className="text-primary hover:underline">
              {o["ad_title"]}
            </Link>
          </p>
          <p className="mt-1 text-muted-foreground">{deliveryLabel(o["delivery_method"])}</p>
          <dl className="mt-3 space-y-1">
            <div className="flex justify-between">
              <dt>Pryl</dt>
              <dd>{kr(o["item_amount"])}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Frakt</dt>
              <dd>{kr(o["shipping_amount"])}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Avgift</dt>
              <dd>{kr(o["fee_amount"])}</dd>
            </div>
            <div className="flex justify-between font-semibold">
              <dt>Totalt</dt>
              <dd>{kr(o["total_amount"])}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Utbetalning</dt>
              <dd>{kr(o["payout_amount"])}</dd>
            </div>
          </dl>
        </section>

        <section className="rounded-xl border border-border bg-card p-4 text-sm">
          <h2 className="font-heading text-lg font-semibold">Parter</h2>
          <p className="mt-2">
            <span className="text-muted-foreground">Köpare: </span>
            {o["buyer_name"] || "–"} {data.buyerEmail && `(${data.buyerEmail})`}
          </p>
          <p>
            <span className="text-muted-foreground">Säljare: </span>
            {data.sellerEmail || o["seller_id"]}
          </p>
          {o["buyer_address"] && (
            <p className="mt-1">
              <span className="text-muted-foreground">Adress: </span>
              {o["buyer_address"]}
            </p>
          )}
          {o["buyer_note"] && (
            <p className="mt-1">
              <span className="text-muted-foreground">Meddelande: </span>
              {o["buyer_note"]}
            </p>
          )}
          {o["tracking_number"] && (
            <p className="mt-1 font-mono text-xs">Kolli: {o["tracking_number"]}</p>
          )}
          {o["dispute_reason"] && (
            <p className="mt-2 rounded-md bg-destructive/10 p-2 text-destructive">
              Tvist: {o["dispute_reason"]}
            </p>
          )}
          {o["label_qr"] && (
            <div className="mt-3">
              <SimulatedQr value={o["label_qr"]} size={110} />
            </div>
          )}
        </section>
      </div>

      <section className="mt-6 rounded-xl border border-border bg-card p-4">
        <h2 className="font-heading text-lg font-semibold">Åtgärder</h2>
        <Textarea
          className="mt-3"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Intern notering (frivillig)"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {ACTIONS.map((a) => (
            <Button
              key={a.action}
              variant="outline"
              size="sm"
              disabled={mut.isPending}
              onClick={() => mut.mutate(a.action)}
            >
              {a.label}
            </Button>
          ))}
        </div>
      </section>

      <section className="mt-6 rounded-xl border border-border bg-card p-4">
        <h2 className="font-heading text-lg font-semibold">Frakt och spårning</h2>
        {!shipment?.tracking ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Ingen fraktsedel skapad än, så det finns inget kolli att spåra.
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm">
              <span className="text-muted-foreground">Kollinummer: </span>
              <span className="font-mono">{shipment.tracking}</span>{" "}
              <Link
                to="/order-sparning/$tracking"
                params={{ tracking: shipment.tracking }}
                className="text-primary hover:underline"
              >
                publik spårning
              </Link>
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {shipment.flow.map((s) => (
                <Button
                  key={s}
                  variant="outline"
                  size="sm"
                  disabled={stepMut.isPending}
                  onClick={() => stepMut.mutate(s)}
                >
                  {shipmentStatusLabel(s)}
                </Button>
              ))}
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              {shipment.events.length === 0 && (
                <li className="text-muted-foreground">Inga kollihändelser än.</li>
              )}
              {shipment.events.map((e) => (
                <li key={`${e.status}-${e.occurred_at}`} className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">{shipmentStatusLabel(e.status)}</span>
                  <span className="text-muted-foreground">
                    {new Date(e.occurred_at).toLocaleString("sv-SE")}
                    {e.place && ` · ${e.place}`}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="mt-6 rounded-xl border border-border bg-card p-4">
        <h2 className="font-heading text-lg font-semibold">Händelselogg</h2>
        <ul className="mt-3 space-y-2 text-sm">
          {(data.events as any[]).map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium">{statusLabel(e.status)}</span>
              <span className="text-xs uppercase text-muted-foreground">{e.actor}</span>
              <span className="text-muted-foreground">
                {new Date(e.created_at).toLocaleString("sv-SE")}
              </span>
              {e.note && <span className="w-full text-muted-foreground">{e.note}</span>}
            </li>
          ))}
        </ul>
      </section>
    </AdminShell>
  );
}
